/**
 * Compile-time Remote Config defaults.
 *
 * Mirrors Flutter's `RemoteConfigsDefaults`
 * (lib/domain/enums/remote_configs_defaults.dart:1-43) filtered through the map
 * Flutter actually hands to `setDefaults`
 * (lib/services/remote_config_service.dart:120-175) — **30 entries**, declared
 * here in that same order — plus the 6 web-only tokens from
 * `WebRemoteConfigs`. 36 entries total.
 *
 * ## Deliberate omissions — do NOT "fix" them
 *
 * The 12 `version*` keys (`versionGifts`, `versionRechargeWalletProducts`,
 * `versionPremiumPackages`, `versionDefaultSettings`, `versionTermsOfService`,
 * `versionCoinsPolicy`, `versionCommunityGuidelines`,
 * `versionExclusiveSubscriptionsPolicy`, `versionPremiumSubscriptions`,
 * `versionPrivacyPolicy`, `versionRewardsPolicy`, `versionDonationProducts`)
 * and `boosts` have **no** entry below. Flutter omits them from its own
 * defaults map for the same reason (remote_config_service.dart:120-175): they
 * are server-only and only meaningful through the real-time update listener.
 * `boosts` consequently reads back as `''` → an empty boost list, exactly
 * matching Flutter's empty-string guard (remote_config_service.dart:86-88).
 *
 * ## The `Partial<…>` annotation is the load-bearing contract — do not tighten it
 *
 * The map is annotated exactly:
 *
 *     Partial<Record<RemoteConfigKey, string | number | boolean>>
 *
 * `RemoteConfigKey` has 43 + 6 = 49 members while this map holds 36, so a total
 * `Record<RemoteConfigKey, …>` annotation does not compile under `strict: true`
 * (TS2739, missing properties), and an un-annotated object literal cannot be
 * indexed by an arbitrary `RemoteConfigKey` either (TS7053). With `Partial<…>`,
 * `REMOTE_CONFIG_DEFAULTS[key]` is `string | number | boolean | undefined` for
 * any `RemoteConfigKey` — and that `undefined` branch is precisely the fallback
 * path the domain facade's read helpers depend on (`boosts` is the only key that
 * reaches them with no default). `tsconfig.json` sets neither
 * `noUncheckedIndexedAccess` nor `exactOptionalPropertyTypes`, so the
 * `| undefined` comes solely from the optional properties.
 *
 * The map is handed straight to
 * `remoteConfigDataService.initialize(defaults: Record<string, string | number | boolean | undefined>)`,
 * whose parameter type admits those `undefined`-valued optional properties and
 * which drops them before assigning Firebase's `defaultConfig` — so **no cast is
 * needed anywhere**; do not add `as Record<…>` to make it fit.
 *
 * ## Flutter alignment — zero value divergences
 *
 * Every value below was diffed against
 * lib/domain/enums/remote_configs_defaults.dart and is value-identical to it.
 * Two entries were divergent before this branch and are now closed by the paired
 * Flutter change (authorised by the branch task prompt lines 33 / 35):
 *
 * 1. `isTestMode` — web `false`; Flutter flipped `true` → `false`
 *    (remote_configs_defaults.dart:9).
 * 2. `lastSeenEnabled` — web `true`; Flutter flipped `false` → `true`
 *    (remote_configs_defaults.dart:29).
 */

import { RemoteConfigs, WebRemoteConfigs, type RemoteConfigKey } from '@/domain/enums/remoteConfigs';

/**
 * Compile-time seed for a NEW user document's `subscription_charge`.
 *
 * Flutter uses the compile-time const here, NOT the live Remote Config value:
 * `User({ this.subscriptionCharge = RemoteConfigsDefaults.exclusiveSubscriptionDefaultDollars })`
 * (lib/data/models/user.dart:97-98 → lib/domain/enums/remote_configs_defaults.dart:19,
 * `static const double exclusiveSubscriptionDefaultDollars = 5`). It is one of only
 * four `RemoteConfigsDefaults` members declared `static const` rather than
 * `static get`, precisely so it can serve as a Dart compile-time default-parameter
 * value; the same const is the read-side fallback in the generated deserializer
 * (lib/data/models/user.g.dart:89-90). Flutter's live getter
 * `RemoteConfigService.exclusiveSubscriptionDefaultDollars`
 * (lib/services/remote_config_service.dart:48-49) has zero callers, so seeding the
 * persisted document from it would diverge the two clients as soon as the console
 * template is retuned.
 */
export const EXCLUSIVE_SUBSCRIPTION_DEFAULT_DOLLARS = 5;

export const REMOTE_CONFIG_DEFAULTS: Partial<Record<RemoteConfigKey, string | number | boolean>> = {
  // ── Flutter parity (remote_config_service.dart:120-175, in declaration order) ──

  /**
   * Flutter: `false` (remote_configs_defaults.dart:2).
   *
   * **No web consumer by design.** The single Flutter consumer is the iOS
   * "Sign in with Apple" fallback (lib/presentation/screens/login/login_screen.dart:348),
   * which has no web meaning. The key and its default are carried for template
   * parity only — the domain facade intentionally exposes **no getter** for it.
   */
  [RemoteConfigs.useGoogleLoginOnApple]: false,

  /** Flutter: `true` (remote_configs_defaults.dart:3). Kill-switch for video init/play. */
  [RemoteConfigs.initVideos]: true,

  /** Flutter: `false` (remote_configs_defaults.dart:4). Ops kill-switch for infrastructure spend. */
  [RemoteConfigs.budgetExceeded]: false,

  /**
   * Flutter: `false` (remote_configs_defaults.dart:9) — flipped from `true` by
   * the paired Flutter change on this branch so both clients agree before the
   * first fetch lands.
   */
  [RemoteConfigs.isTestMode]: false,

  /** Flutter: `"USD"` (remote_configs_defaults.dart:10). */
  [RemoteConfigs.defaultCurrency]: 'USD',

  /** Flutter: `100` (remote_configs_defaults.dart:11). */
  [RemoteConfigs.dollarToCoinsRatio]: 100,

  /**
   * Flutter: `0.5` (remote_configs_defaults.dart:12).
   *
   * **No web consumer by design.** The only Flutter consumer is
   * `MathUtils.coinsToDiamonds` (lib/presentation/utils/math_utils.dart:157);
   * `domain/utils/mathUtils.ts` has no coins→diamonds conversion.
   */
  [RemoteConfigs.coinsToDiamondsRatio]: 0.5,

  /** Flutter: `0.01` (remote_configs_defaults.dart:13). */
  [RemoteConfigs.diamondsToDollarsRatio]: 0.01,

  /**
   * Flutter: `100` (remote_configs_defaults.dart:14).
   *
   * **No web consumer by design** — there is no web withdraw screen. Flutter's
   * consumers are lib/presentation/screens/wallet/withdraw_wallet_screen.dart:69-78
   * and lib/presentation/utils/payment_utils.dart:23.
   */
  [RemoteConfigs.withdrawMinDollars]: 100,

  /**
   * Flutter: `1000` (remote_configs_defaults.dart:15).
   *
   * **No web consumer by design** — see `withdrawMinDollars`.
   */
  [RemoteConfigs.withdrawMaxDollars]: 1000,

  /**
   * Flutter: `100` (remote_configs_defaults.dart:16).
   *
   * **No web consumer by design** — Flutter uses it in the withdraw screen and
   * in the `{withdrawAutoDollars}` settings-description placeholder
   * (lib/domain/constants/placeholder_constants.dart:6-7,
   * lib/presentation/utils/payment_utils.dart:24); the web has neither.
   */
  [RemoteConfigs.withdrawAutoDollars]: 100,

  /** Flutter: `0` (remote_configs_defaults.dart:17). */
  [RemoteConfigs.exclusiveSubscriptionMinDollars]: 0,

  /** Flutter: `100` (remote_configs_defaults.dart:18). */
  [RemoteConfigs.exclusiveSubscriptionMaxDollars]: 100,

  /** Flutter: `5` (remote_configs_defaults.dart:19). */
  [RemoteConfigs.exclusiveSubscriptionDefaultDollars]: EXCLUSIVE_SUBSCRIPTION_DEFAULT_DOLLARS,

  /** Flutter: `1` (remote_configs_defaults.dart:20). */
  [RemoteConfigs.contentUnlockMinDollars]: 1,

  /** Flutter: `100` (remote_configs_defaults.dart:21). */
  [RemoteConfigs.contentUnlockMaxDollars]: 100,

  /** Flutter: `5` (remote_configs_defaults.dart:22). */
  [RemoteConfigs.contentUnlockDefaultDollars]: 5,

  /** Flutter: `60` (remote_configs_defaults.dart:23). */
  [RemoteConfigs.preferredContentTagsWatchMinSeconds]: 60,

  /** Flutter: `5` (remote_configs_defaults.dart:30). */
  [RemoteConfigs.updateViewsWatchMinSeconds]: 5,

  /** Flutter: `5` (remote_configs_defaults.dart:31). */
  [RemoteConfigs.saveToWatchHistoryMinSeconds]: 5,

  /**
   * 10.5 minutes. Flutter: `10500000` (remote_configs_defaults.dart:32-34 —
   * "should be 10 mins; allow some buffer").
   *
   * **No web consumer by design** — the threshold is applied Cloud-Functions-side,
   * where it is mirrored as `FILM_MILLIS_MIN`
   * (cloud_functions/functions/src/functions/common/app_constants.ts:234).
   * The three copies must stay identical: **do not change the value.**
   */
  [RemoteConfigs.filmMillisMin]: 10500000,

  /** Flutter: `75` (remote_configs_defaults.dart:24). */
  [RemoteConfigs.preferredContentTagsWatchMinPercentage]: 75,

  /**
   * Flutter: `true` (remote_configs_defaults.dart:29) — flipped from `false` by
   * the paired Flutter change on this branch so both clients agree before the
   * first fetch lands.
   */
  [RemoteConfigs.lastSeenEnabled]: true,

  /** Flutter: `15` (remote_configs_defaults.dart:35). */
  [RemoteConfigs.contentMinSeconds]: 15,

  /** Flutter: `5400` — 90 minutes (remote_configs_defaults.dart:36). */
  [RemoteConfigs.contentMaxSeconds]: 5400,

  /** Flutter: `600` — 10 minutes (remote_configs_defaults.dart:37). */
  [RemoteConfigs.freeContentMaxSeconds]: 600,

  /** Flutter: `10` coins per minute (remote_configs_defaults.dart:38). */
  [RemoteConfigs.premiumContentCoinsPerMinute]: 10,

  /** Flutter: `3` (remote_configs_defaults.dart:39). */
  [RemoteConfigs.contentScheduleMinHours]: 3,

  /**
   * Flutter: `719` (remote_configs_defaults.dart:40-41). 30 days — the Cloud
   * Tasks maximum — is 720 hours; 1 hour is subtracted so the limit is never
   * reached.
   */
  [RemoteConfigs.contentScheduleMaxHours]: 719,

  /**
   * Flutter: `false` (remote_configs_defaults.dart:42).
   *
   * **No web consumer by design** — the web content list owns its own autoplay
   * policy and has no equivalent hook. The only Flutter consumer is
   * `ContentUtils.shouldAutoInitVideos`
   * (lib/presentation/utils/content_utils.dart:107-108).
   */
  // TODO: @claude add a follow up task for this: port ContentUtils.shouldAutoInitVideos auto-init gating to the web content list — Flutter lib/presentation/utils/content_utils.dart:107-108
  [RemoteConfigs.shouldAutoInitVideos]: false,

  // ── Web-only tokens (no Flutter counterpart; see WebRemoteConfigs) ──

  /** Carried over from the retired `remoteConfigConstants.ts:14`. */
  [WebRemoteConfigs.webNativeFullscreenMode]: true,

  /** Carried over from the retired `remoteConfigConstants.ts:17`. */
  [WebRemoteConfigs.shareContentMaxCharacters]: 280,

  /** Carried over from the retired `remoteConfigConstants.ts:18`. */
  [WebRemoteConfigs.shareUserMaxCharacters]: 280,

  /** 5 seconds. Carried over from the retired `remoteConfigConstants.ts:21`. */
  [WebRemoteConfigs.chatContentMinSeconds]: 5,

  /** 5 minutes. Carried over from the retired `remoteConfigConstants.ts:22`. */
  [WebRemoteConfigs.chatContentMaxSeconds]: 300,

  /** Carried over from the retired `remoteConfigConstants.ts:33`. */
  [WebRemoteConfigs.contentTypeLimit]: 15000,
};

export type RemoteConfigDefaults = typeof REMOTE_CONFIG_DEFAULTS;
